import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";

export default function PasswordNew() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState(searchParams.get("email") ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const next: Record<string, string> = {};
    if (!email.trim()) next.email = "L'email est requis";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = "Adresse email invalide";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    if (!validate()) {
      e.preventDefault();
      return;
    }
    setSubmitting(true);
    // Le formulaire se soumet nativement (POST /users/password → redirection HTTP)
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-md mx-4 bg-background rounded-2xl shadow-2xl border border-border p-8 animate-in zoom-in-95 duration-200">
        {/* Croix → accueil */}
        <button
          onClick={() => navigate("/")}
          className="absolute right-4 top-4 rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
          aria-label="Retour à l'accueil"
        >
          <X className="h-5 w-5" />
        </button>

        {/* En-tête */}
        <div className="text-center space-y-2 mb-6">
          <h1 className="text-2xl font-display bg-gradient-to-r from-[#f7bbac] to-[#f9bb72] bg-clip-text text-transparent">
            Mot de passe oublié
          </h1>
          <p className="text-sm text-muted-foreground">
            Saisissez votre adresse email, nous vous enverrons un lien pour réinitialiser votre mot de passe
          </p>
        </div>

        {/* Formulaire — POST natif pour redirection HTTP */}
        <form method="POST" action="/users/password" onSubmit={handleSubmit} className="space-y-4">
          <input type="hidden" name="authenticity_token" value={csrf} />

          <div className="space-y-2">
            <Label htmlFor="email">Adresse email</Label>
            <Input
              id="email"
              name="user[email]"
              type="email"
              placeholder="votre@email.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors({ ...errors, email: "" });
              }}
              className={errors.email ? "border-destructive" : ""}
            />
            {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
          </div>

          <Button
            type="submit"
            variant="rainbow"
            size="lg"
            className="w-full font-medium"
            disabled={submitting}
          >
            {submitting ? "Envoi..." : "Envoyer le lien"}
          </Button>
        </form>

        {/* Liens */}
        <div className="mt-6 space-y-3 text-center text-sm">
          <Link
            to="/users/sign_in"
            className="block text-[#f7bbac] hover:text-[#f7bbac]/80 transition-smooth underline-offset-4 hover:underline font-medium"
          >
            Déjà inscrit ?
          </Link>

          <div className="text-muted-foreground">
            Pas encore de compte ?{" "}
            <Link
              to="/users/sign_up"
              className="text-[#f7bbac] hover:text-[#f7bbac]/80 transition-smooth underline-offset-4 hover:underline font-medium"
            >
              S'inscrire
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
